import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  LinearProgress,
  TextField,
  Stack,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { Snackbar, Alert } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { treeItemClasses } from "@mui/x-tree-view/TreeItem";
import { TreeItem2, TreeItem2Props } from "@mui/x-tree-view";
import { useTreeItem2, UseTreeItem2ReturnValue } from "@mui/x-tree-view";
import { useLazyGetCauseQuery } from "../../../services/minute";
import { v4 as uuidv4 } from "uuid";

interface CustomLabelProps {
  children: string;
  className: string;
  numberOfChildren: number;
  isLoading: boolean;
}

function CustomLabel({
  children,
  className,
  numberOfChildren,
  isLoading,
}: CustomLabelProps) {
  return (
    <Stack
      direction="row"
      justifyContent="flex-start"
      spacing={2}
      flexGrow={1}
      className={className}
    >
      <Typography>{children}</Typography>
      {numberOfChildren > 0 && (
        <Chip label={numberOfChildren} size="small" sx={{ ml: 1 }} />
      )}
      {isLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
    </Stack>
  );
}

const StyledTreeItem2 = styled(TreeItem2)(({ theme }) => ({
  [`& .${treeItemClasses.iconContainer}`]: {
    borderRadius: "50%",
    backgroundColor: theme.palette.secondary.dark,
    padding: theme.spacing(0, 1.2),
    ...theme.applyStyles("light", {
      backgroundColor: alpha(theme.palette.secondary.main, 0.25),
    }),
    ...theme.applyStyles("dark", {
      color: theme.palette.secondary.contrastText,
    }),
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginLeft: 15,
    paddingLeft: 18,
    borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
  },
}));

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
  props: TreeItem2Props & { isLoading?: boolean; errorItemId?: string | null },
  ref: React.Ref<HTMLLIElement>
) {
  const { publicAPI } = useTreeItem2(props) as UseTreeItem2ReturnValue<
    any,
    any
  > & {
    publicAPI: { getItemOrderedChildrenIds: (itemId: string) => string[] };
  };
  const childrenNumber = publicAPI.getItemOrderedChildrenIds(
    props.itemId
  ).length;

  return (
    <>
      <StyledTreeItem2
        {...props}
        ref={ref}
        slots={{ label: CustomLabel }}
        slotProps={{
          label: {
            numberOfChildren: childrenNumber,
            isLoading: props.isLoading,
          } as CustomLabelProps,
        }}
        style={
          props.itemId === props.errorItemId
            ? { color: "red", pointerEvents: "none" }
            : {}
        }
      >
        {props.children}
      </StyledTreeItem2>
      {props.isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 1,
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
    </>
  );
});

const CausePage = () => {
  const location = useLocation();
  const [keyword, setKeyword] = useState(
    new URLSearchParams(location.search).get("keyword") || ""
  );
  const [triggerGetCause, { data: apiData, error, isLoading }] =
    useLazyGetCauseQuery();
  const [isKeywordChanged, setIsKeywordChanged] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [loadingItems, setLoadingItems] = useState<boolean>(false);
  const [reLoading, setReLoading] = useState(false); // 検索中の状態を管理するための状態
  const [popupOpen, setPopupOpen] = useState(false);
  const [errorItemId, setErrorItemId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // `setData`の宣言
  const [data, setData] = useState<{
    assignment_cause: {
      assignment: string;
      cause_list: { id: string; cause: string }[];
    };
  } | null>(null);

  useEffect(() => {
    const fetchData = async (retryCount = 0) => {
      try {
        const response = await triggerGetCause({ assignment_word: keyword });
        console.log("responce", response);
        if (response.error) {
          const error = response.error as any;
          console.log("responce", response.error);
          if (error.status === "FETCH_ERROR" && retryCount < 2) {
            console.log(`再試行中... (${retryCount + 1}回目)`);
            fetchData(retryCount + 1); // すぐに再試行
          } else if (error.status === "FETCH_ERROR") {
            setPopupOpen(true);
            setErrorMessage("サーバータイムアウト:もう一度実行してください");
            setIsKeywordChanged(true);
          } else {
            setPopupOpen(true);
            setErrorMessage("サーバーエラー");
            setIsKeywordChanged(true);
          }
        } else {
          // データ取得が成功した場合、特に何も出力しない
        }
      } catch (error) {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました");
        console.log("予期せぬエラーが発生しました:", error);
      }
    };

    if (keyword) {
      fetchData();
    }
  }, [triggerGetCause]);

  // `useEffect`で`apiData`を`data`に設定
  useEffect(() => {
    if (apiData) {
      if (
        apiData &&
        apiData.assignment_cause &&
        apiData.assignment_cause.cause_list
      ) {
        if (
          apiData.assignment_cause.cause_list.some(
            (cause: any) => cause.cause === "原因を推定できません"
          )
        ) {
          setPopupOpen(true);
          setErrorMessage("原因を推定できませんでした");
          const errorCause = apiData.assignment_cause.cause_list.find(
            (cause: any) => cause.cause === "原因を推定できません"
          );
          if (errorCause) {
            setErrorItemId(errorCause.id);
          }
        }
        const addUuidToData = (data: any) => {
          return {
            ...data,
            assignment_cause: {
              ...data.assignment_cause,
              cause_list: data.assignment_cause.cause_list
                .filter((cause: any) => cause.cause !== "原因を推定できません") // "原因を推定できません"を除外
                .map((cause: any) => ({
                  ...cause,
                  id: uuidv4(), // ユニークなIDを生成
                  label: cause.cause,
                  children: cause.children ? addUuidToData(cause.children) : [],
                })),
            },
          };
        };
        setData((prevData) => {
          if (!prevData) return addUuidToData(apiData);
          return prevData;
        });
      }
    }
  }, [apiData]);

  const history = useHistory();
  const handleReExecute = (
    event?: React.MouseEvent<HTMLButtonElement>,
    retryCount = 0
  ) => {
    if (event) {
      event.preventDefault();
    }
    setIsKeywordChanged(false);
    setReLoading(true); // 検索中の状態にする
    setData(null); // データをクリア
    const url = `/cause?keyword=${encodeURIComponent(keyword)}`;
    history.push(url);
    triggerGetCause({ assignment_word: keyword })
      .then((response) => {
        if (response.error) {
          const error = response.error as any;
          if (error.status === "FETCH_ERROR" && retryCount < 2) {
            console.log(`再試行中... (${retryCount + 1}回目)`);
            handleReExecute(undefined, retryCount + 1); // すぐに再試行
          } else if (error.status === "FETCH_ERROR") {
            setPopupOpen(true);
            setErrorMessage("サーバータイムアウト:もう一度実行してください");
            setReLoading(false); // 最後の三回目が終わったタイミングで状態を解除
            setIsKeywordChanged(true);
          } else {
            setPopupOpen(true);
            setErrorMessage("サーバーエラー");
            setReLoading(false); // サーバーエラーで状態を解除
            setIsKeywordChanged(true);
          }
        } else {
          setReLoading(false); // データ取得が成功した場合に状態を解除
        }
      })
      .catch((error) => {
        setPopupOpen(true);
        setErrorMessage("予期せぬエラーが発生しました");
        console.log("予期せぬエラーが発生しました:", error);
        setReLoading(false); // 予期せぬエラーを受けたときに状態を解除
      });
  };

  const handleKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
    setIsKeywordChanged(true);
  };

  const handleItemClick = async (
    event: React.MouseEvent,
    itemId: string,
    retryCount = 0
  ) => {
    if (window.getSelection()?.toString()) return; // 範囲選択時は実行しない
    event.stopPropagation(); // イベントのバブリングを防ぐ
    if (loadingItems) return; // データ取得中はクリックを無効にする

    const findItemById = (items: any[], id: string): any | null => {
      for (const item of items) {
        if (item.id === id) return item; // idを使用して検索
        if (item.children) {
          const found = findItemById(item.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const item = findItemById(data?.assignment_cause.cause_list || [], itemId);
    if (item) {
      if (!item.children || item.children.length === 0) {
        setLoadingItems(true); // 子要素がない場合のみloadingItemsをtrueに設定
        try {
          const response = await triggerGetCause({
            assignment_word: item.cause,
          });
          if (response.error) {
            const error = response.error as any;
            if (error.status === "FETCH_ERROR" && retryCount < 2) {
              console.log(`再試行中... (${retryCount + 1}回目)`);
              handleItemClick(event, itemId, retryCount + 1); // すぐに再試行
            } else if (error.status === "FETCH_ERROR") {
              setPopupOpen(true);
              setErrorMessage("サーバータイムアウト:もう一度実行してください");
              setLoadingItems(false); // 最後の三回目が終わったタイミングで状態を解除
            } else {
              setPopupOpen(true);
              setErrorMessage("サーバーエラー");
              setLoadingItems(false); // サーバーエラーで状態を解除
            }
          } else {
            const newData = response.data;
            if (
              newData &&
              newData.assignment_cause &&
              newData.assignment_cause.cause_list
            ) {
              if (
                newData.assignment_cause.cause_list.some(
                  (cause: any) => cause.cause === "原因を推定できません"
                )
              ) {
                setPopupOpen(true);
                setErrorMessage("原因を推定できませんでした");
                setErrorItemId(itemId);
              } else {
                const addUuidToData = (data: any) => {
                  return data.assignment_cause.cause_list.map((cause: any) => ({
                    ...cause,
                    id: uuidv4(), // ユニークなIDを生成
                    label: cause.cause,
                    children: cause.children
                      ? addUuidToData(cause.children)
                      : [],
                  }));
                };

                setData((prevData) => {
                  if (!prevData) return prevData;
                  const updateChildren = (items: any[]): any[] => {
                    return items.map((item) => {
                      if (item.id === itemId) {
                        return {
                          ...item,
                          children: addUuidToData(newData),
                        };
                      }
                      if (item.children) {
                        return {
                          ...item,
                          children: updateChildren(item.children),
                        };
                      }
                      return item;
                    });
                  };
                  return {
                    ...prevData,
                    assignment_cause: {
                      ...prevData.assignment_cause,
                      cause_list: updateChildren(
                        prevData.assignment_cause.cause_list
                      ),
                    },
                  };
                });
              }
            }
            setLoadingItems(false); // データ取得が成功した場合に状態を解除
          }
        } catch (error) {
          setPopupOpen(true);
          setErrorMessage("予期せぬエラーが発生しました");
          console.log("予期せぬエラーが発生しました:", error);
          setLoadingItems(false); // 予期せぬエラーを受けたときに状態を解除
        }
      }
    }
  };

  const items = data
    ? [
        {
          id: data.assignment_cause.assignment,
          label: data.assignment_cause.assignment,
          children: data.assignment_cause.cause_list.map((cause: any) => ({
            id: cause.id,
            label: cause.cause,
            children: cause.children || [],
            isLoading:
              loadingItems && (!cause.children || cause.children.length === 0), //　子要素がない場合のみloadingItemsを使用
          })),
        },
      ]
    : [];

  return (
    <div>
      {(isLoading || loadingItems || reLoading) && (
        <Box
          sx={{
            position: "absolute",
            top: "60%",
            right: "50%",
            zIndex: 1300, // MUIのDialogと同等のz-index
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <AppBar position="static" color="secondary" sx={{ mb: 2 }}>
        <Toolbar>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flexGrow: 1 }}
          >
            <Typography variant="subtitle1" sx={{ ml: 2 }}>
              課題：
            </Typography>
            <TextField
              value={keyword}
              onChange={handleKeywordChange}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (
                    !isLoading &&
                    !loadingItems &&
                    !reLoading &&
                    isKeywordChanged &&
                    keyword.trim() !== ""
                  ) {
                    handleReExecute();
                  }
                }
              }}
              size="small"
              variant="outlined"
              multiline
              sx={{ width: "40%", backgroundColor: "white", ml: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleReExecute}
              disabled={
                !isKeywordChanged ||
                keyword.trim() === "" ||
                isLoading ||
                loadingItems ||
                reLoading
              }
              sx={{
                backgroundColor: "#4caf50",
                color: "white",
                ml: 2,
                "&:hover": {
                  backgroundColor: "#388e3c",
                },
              }}
            >
              分析
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box sx={{ display: "flex", flexDirection: "column", mb: 2 }}>
        <h3>なぜなぜ分析</h3>
      </Box>
      <Snackbar
        open={popupOpen}
        autoHideDuration={5000}
        onClose={() => setPopupOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // 画面中央に表示
      >
        <Alert
          onClose={() => setPopupOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      {(isLoading || loadingItems || reLoading) && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress color="secondary" />
        </Box>
      )}
      {!isLoading && !apiData && keyword && <p>データがありません</p>}
      {!isLoading && !apiData && !keyword && <p>課題が指定されていません</p>}
      {apiData && (
        <Box sx={{ minHeight: 352, minWidth: 250 }}>
          <RichTreeView
            defaultExpandedItems={[apiData.assignment_cause.assignment]}
            items={items}
            slots={{ item: CustomTreeItem }}
            onItemClick={handleItemClick}
          />
        </Box>
      )}
    </div>
  );
};

export default CausePage;
